import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ArrowIcon from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as PenIcon } from '../../../assets/icons/Pen.svg';
import {
  getStatusGuides,
  StatusGuide,
  StatusGuideEntry,
  updateStatusGuideEntry,
} from '../../../api/appCms';
import Button from '../../BaseComponents/Button';
import Modal from '../../BaseComponents/Modal';
import TextArea from '../../BaseComponents/TextArea';
import './AppCmsTab.scss';

const AppCmsTab = () => {
  const [guides, setGuides] = useState<StatusGuide[]>([]);
  const [expandedGuides, setExpandedGuides] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<{
    guide: StatusGuide;
    entry: StatusGuideEntry;
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchGuides = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getStatusGuides();
      setGuides(response.guides);
      setExpandedGuides((prev) => {
        const next = { ...prev };
        response.guides.forEach((guide) => {
          if (next[guide.guide_type] === undefined) {
            next[guide.guide_type] = true;
          }
        });
        return next;
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load App CMS content. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const toggleGuide = (guideType: string) => {
    setExpandedGuides((prev) => ({
      ...prev,
      [guideType]: !prev[guideType],
    }));
  };

  const handleEdit = (guide: StatusGuide, entry: StatusGuideEntry) => {
    setEditingEntry({ guide, entry });
    setEditValue(entry.description);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingEntry(null);
    setEditValue('');
  };

  const handleSave = async () => {
    if (!editingEntry || isSaving) return;

    setIsSaving(true);
    try {
      const updatedGuide = await updateStatusGuideEntry({
        guide_type: editingEntry.guide.guide_type,
        entry_key: editingEntry.entry.key,
        description: editValue,
      });

      setGuides((prev) =>
        prev.map((guide) =>
          guide.guide_type === updatedGuide.guide_type ? updatedGuide : guide
        )
      );

      toast.success('Description updated successfully');
      handleCloseModal();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to update description. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="app-cms-tab-container">
        <div className="search-row-container">
          <div className="search-and-filter-container">
            <div className="count-container">
              App CMS ({guides.length} guides)
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="app-cms-loading">Loading App CMS content...</div>
        ) : (
          <div className="app-cms-guides">
            {guides.map((guide) => {
              const isExpanded = expandedGuides[guide.guide_type];

              return (
                <div
                  key={guide.guide_type}
                  className={`app-cms-guide-section${isExpanded ? ' expanded' : ''}`}
                >
                  <div
                    className="app-cms-guide-header"
                    onClick={() => toggleGuide(guide.guide_type)}
                  >
                    <h3>{guide.label}</h3>
                    <img
                      src={ArrowIcon}
                      alt=""
                      className={`app-cms-guide-arrow${isExpanded ? ' expanded' : ''}`}
                    />
                  </div>

                  {isExpanded && (
                    <div className="app-cms-guide-content">
                      {guide.entries.map((entry) => (
                        <div key={entry.key} className="app-cms-entry">
                          <div className="app-cms-entry-header">
                            <h4 className="app-cms-entry-title">{entry.label}</h4>
                            <button
                              type="button"
                              className="app-cms-edit-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(guide, entry);
                              }}
                            >
                              <PenIcon className="app-cms-edit-icon" />
                              Edit
                            </button>
                          </div>
                          <div className="app-cms-entry-description">
                            {entry.description || (
                              <span className="app-cms-entry-empty">No description set</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isEditModalOpen && editingEntry && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          title={`Edit ${editingEntry.entry.label}`}
          width="640px"
        >
          <div className="app-cms-edit-modal">
            <TextArea
              title="Description"
              name="status_guide_description"
              value={editValue}
              placeholder={`Enter description for ${editingEntry.entry.label}...`}
              rows={8}
              isDisabled={isSaving}
              onChange={(e) => setEditValue(e.target.value)}
            />

            <div className="app-cms-edit-actions">
              <Button
                text="Cancel"
                onClick={handleCloseModal}
                isSecondary={true}
                isDisabled={isSaving}
                backgroundColor="#ffffff"
                color="#2D334E"
              />
              <Button
                text={isSaving ? 'Saving…' : 'Save'}
                onClick={handleSave}
                isDisabled={isSaving}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AppCmsTab;
